import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Scissors, Heart, Users, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { organizationData, track1Data, track2Data, testimonials } from '../mock';
import { subscribeNewsletter } from '../services/api';
import { useToast } from '../hooks/use-toast';

const Home = () => {
  const [email, setEmail] = useState('');
  const { toast } = useToast();

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    const result = await subscribeNewsletter(email);
    if (result.success) {
      toast({
        title: "Success!",
        description: result.message,
      });
      setEmail('');
    } else {
      toast({
        title: "Error",
        description: result.message,
        variant: "destructive"
      });
    }
  };

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        {/* Background Image */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1622675103136-e4b90c9a33d6"
            alt="MOS Veteran Community"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A277A]/95 via-[#0A277A]/85 to-transparent"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-[87.5rem] mx-auto px-5 md:px-10 py-20">
          <div className="max-w-3xl">
            <div className="inline-block bg-[#D80000] text-white px-4 py-2 rounded-full text-sm font-bold uppercase mb-6">
              Nonprofit Ecosystem
            </div>
            <h1 className="font-black text-5xl md:text-7xl leading-tight text-white uppercase mb-6">
              {organizationData.tagline}
            </h1>
            <p className="text-white text-xl md:text-2xl font-bold mb-6 leading-snug">
              {organizationData.mission}
            </p>
            <p className="text-white/90 text-lg mb-10 leading-relaxed">
              From the chair to the classroom — MOS Academy empowers veterans and families to master their purpose, their skills, and their story.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/track-1">
                <Button className="bg-white text-[#0A277A] hover:bg-gray-100 rounded-full px-8 py-6 text-base font-semibold uppercase transition-all hover:scale-105 shadow-lg">
                  Personal Mastery
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/track-2">
                <Button className="bg-[#D80000] text-white hover:bg-[#b00000] rounded-full px-8 py-6 text-base font-semibold uppercase transition-all hover:scale-105 shadow-lg">
                  Mastery Through Trade
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Core Vision */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-black text-4xl md:text-5xl text-[#0A277A] uppercase mb-6">
              Core Vision
            </h2>
            <p className="text-gray-700 text-xl leading-relaxed">
              {organizationData.vision}
            </p>
          </div>
        </div>
      </section>

      {/* Two-Track System */}
      <section className="py-20">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="text-center mb-16">
            <h2 className="font-black text-4xl md:text-6xl text-[#0A277A] uppercase mb-4">
              Our Programs
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Two transformative pathways — mindset through literacy, opportunity through trade
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Track 1: Personal Mastery */}
            <Card className="bg-gradient-to-br from-[#0A277A] to-[#3F54C8] text-white overflow-hidden group hover:shadow-2xl transition-all duration-300">
              <div className="p-8 md:p-12">
                <BookOpen className="mb-6" size={48} />
                <div className="text-sm font-bold uppercase tracking-wider mb-2 opacity-90">
                  {track1Data.subtitle}
                </div>
                <h3 className="font-black text-3xl md:text-4xl uppercase mb-4">
                  Track 1
                </h3>
                <h4 className="text-xl font-bold mb-2">Personal Mastery & Development</h4>
                <p className="text-white/90 text-sm uppercase tracking-wide mb-6">
                  {track1Data.tagline}
                </p>
                <p className="text-white/95 leading-relaxed mb-8">
                  {track1Data.description}
                </p>
                <div className="space-y-3 mb-8">
                  {track1Data.programs.slice(0, 3).map((program) => (
                    <div key={program.id} className="flex items-start space-x-3">
                      <div className="bg-white/20 rounded-full p-1.5 mt-0.5">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                      <span className="text-sm font-medium">{program.title}</span>
                    </div>
                  ))}
                </div>
                <Link to="/track-1">
                  <Button className="bg-white text-[#0A277A] hover:bg-gray-100 rounded-full px-6 py-3 font-semibold uppercase transition-all w-full md:w-auto">
                    Explore Track 1
                  </Button>
                </Link>
              </div>
            </Card>

            {/* Track 2: Mastery Through Trade */}
            <Card className="bg-gradient-to-br from-[#D80000] to-[#b00000] text-white overflow-hidden group hover:shadow-2xl transition-all duration-300">
              <div className="p-8 md:p-12">
                <Scissors className="mb-6" size={48} />
                <div className="text-sm font-bold uppercase tracking-wider mb-2 opacity-90">
                  {track2Data.subtitle}
                </div>
                <h3 className="font-black text-3xl md:text-4xl uppercase mb-4">
                  Track 2
                </h3>
                <h4 className="text-xl font-bold mb-2">Mastery Through Trade</h4>
                <p className="text-white/90 text-sm uppercase tracking-wide mb-6">
                  {track2Data.tagline}
                </p>
                <p className="text-white/95 leading-relaxed mb-8">
                  {track2Data.description}
                </p>
                <div className="space-y-3 mb-8">
                  {track2Data.programs.map((program) => (
                    <div key={program.id} className="flex items-start space-x-3">
                      <div className="bg-white/20 rounded-full p-1.5 mt-0.5">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                      <span className="text-sm font-medium">{program.title}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-white/20 px-4 py-2 rounded-full inline-block text-xs font-bold uppercase mb-6">
                  Launching 2026
                </div>
                <Link to="/track-2">
                  <Button className="bg-white text-[#D80000] hover:bg-gray-100 rounded-full px-6 py-3 font-semibold uppercase transition-all w-full md:w-auto">
                    Explore Track 2
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* M.O.S. Impact */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-black text-4xl md:text-6xl text-[#0A277A] uppercase mb-6">
                The M.O.S. Impact
              </h2>
            </div>
            <div className="bg-white p-8 md:p-12 rounded-lg shadow-lg border-l-4 border-[#D80000]">
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                M.O.S. Academy represents the bridge between <span className="font-bold text-[#0A277A]">personal transformation</span> and <span className="font-bold text-[#D80000]">professional empowerment</span>.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                Every participant — from a first-time reader to a licensed barber — is walking the path of <span className="font-bold text-[#0A277A]">Mastery of Self™</span>, building a life anchored in purpose, resilience, and service.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="text-center mb-16">
            <h2 className="font-black text-4xl md:text-6xl text-[#0A277A] uppercase mb-4">
              Success Stories
            </h2>
            <p className="text-gray-600 text-lg">
              Lives transformed through mastery and purpose
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-gray-50 border border-gray-200 p-8 hover:border-[#D80000] transition-all duration-300">
                <div className="text-[#D80000] text-4xl mb-4">"</div>
                <p className="text-gray-700 leading-relaxed mb-6">
                  {testimonial.text}
                </p>
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-[#0A277A] font-semibold">{testimonial.name}</p>
                  <p className="text-gray-600 text-sm">{testimonial.role}</p>
                  <p className="text-gray-500 text-xs uppercase mt-1">{testimonial.program}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0A277A]">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10 text-center">
          <h2 className="font-black text-3xl md:text-5xl text-white uppercase mb-6">
            Support Our Mission
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Help us empower veterans and families through education, vocational training, and community support.
          </p>
          <Link to="/donate">
            <Button className="bg-[#D80000] text-white hover:bg-[#b00000] rounded-full px-10 py-6 text-lg font-semibold uppercase transition-all hover:scale-105 shadow-lg">
              Donate Now
            </Button>
          </Link>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="bg-white border-2 border-[#0A277A] rounded-lg p-12 text-center">
            <h2 className="font-black text-3xl md:text-5xl text-[#0A277A] uppercase mb-4">
              Stay Connected
            </h2>
            <p className="text-gray-700 text-lg mb-8 max-w-2xl mx-auto">
              Get updates on programs, events, and success stories from the MOS community.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto flex gap-3">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="border-gray-300 rounded-full px-6"
              />
              <Button type="submit" className="bg-[#D80000] text-white hover:bg-[#b00000] rounded-full px-8 font-semibold uppercase whitespace-nowrap">
                Subscribe
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;